import {createSlice} from '@reduxjs/toolkit';
import {
    getEmployeeGroupsData,
    getEmployeeParkingEvents,
    deleteEmployeeGroupData,
    getEmployeeParkingEventFilterOptionsData,
} from './employee-parking-actions';
import {
    employeeParkingEventsAdapter,
    employeeParkingGroupsAdapter,
} from './employee-parking-adapters';
import dayjs from 'utils/dayjs';

export const INITIAL_FROM_DATE = dayjs();
export const INITIAL_TO_DATE = dayjs().add(90, 'day');

export interface EmployeeParkingState {
    groups: {
        data: ReturnType<typeof employeeParkingGroupsAdapter.getInitialState>;
        meta: {
            count: number;
            limit: number;
            offset: number;
            nextOffset: number;
        };
        loading: boolean;
    };
    events: {
        data: ReturnType<typeof employeeParkingEventsAdapter.getInitialState>;
        meta: {
            count: number;
            page: number;
            pageSize: number;
        };
        loading: boolean;
        filterOptions: {
            eventTypeIds: {
                label: string;
                id: string;
                value: string;
            }[];
            venueIds: {
                label: string;
                id: string;
                value: string;
            }[];
            isLoading: boolean;
            fromDate: dayjs.Dayjs;
            toDate: dayjs.Dayjs;
        };
        appliedFilters: {
            eventTypeIds: {
                label: string;
                id: string;
                value: string;
            }[];
            venueIds: {
                label: string;
                id: string;
                value: string;
            }[];
            fromDate: dayjs.Dayjs;
            toDate: dayjs.Dayjs;
        };
        searchText: string;
    };
}

const initialState: EmployeeParkingState = {
    groups: {
        data: employeeParkingGroupsAdapter.getInitialState(),
        meta: {
            count: 0,
            limit: 25,
            offset: 0,
            nextOffset: 25,
        },
        loading: false,
    },
    events: {
        data: employeeParkingEventsAdapter.getInitialState(),
        meta: {
            count: 0,
            page: 1,
            pageSize: 10,
        },
        loading: false,
        filterOptions: {
            eventTypeIds: [],
            venueIds: [],
            isLoading: false,
            fromDate: INITIAL_FROM_DATE,
            toDate: INITIAL_TO_DATE,
        },
        appliedFilters: {
            eventTypeIds: [],
            venueIds: [],
            fromDate: INITIAL_FROM_DATE,
            toDate: INITIAL_TO_DATE,
        },
        searchText: '',
    },
};

const employeeParking = createSlice({
    name: 'employeeParking',
    initialState,
    reducers: {
        updateFilters: (state, action) => {
            state.events.appliedFilters = action.payload;
        },
        updateSearchQuery: (state, action) => {
            state.events.searchText = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getEmployeeGroupsData.pending, (state) => {
                state.groups.loading = true;
            })
            .addCase(getEmployeeGroupsData.fulfilled, (state, {payload}) => {
                employeeParkingGroupsAdapter.setAll(
                    state.groups.data,
                    payload.data.results
                );
                state.groups.meta = payload.meta;
                state.groups.loading = false;
            })
            .addCase(deleteEmployeeGroupData.fulfilled, (state, {payload}) => {
                employeeParkingGroupsAdapter.removeOne(
                    state.groups.data,
                    payload
                );
            })
            .addCase(getEmployeeParkingEvents.pending, (state) => {
                state.events.loading = true;
            })
            .addCase(getEmployeeParkingEvents.fulfilled, (state, {payload}) => {
                employeeParkingEventsAdapter.setAll(
                    state.events.data,
                    payload.events
                );
                state.events.meta = {
                    ...state.events.meta,
                    ...payload.meta,
                };
                state.events.loading = false;
            })
            .addCase(getEmployeeParkingEvents.rejected, (state) => {
                state.events.loading = false;
            })
            .addCase(
                getEmployeeParkingEventFilterOptionsData.pending,
                (state) => {
                    state.events.filterOptions.isLoading = true;
                }
            )
            .addCase(
                getEmployeeParkingEventFilterOptionsData.fulfilled,
                (state, {payload}) => {
                    state.events.filterOptions.eventTypeIds =
                        payload.eventTypeIds;
                    state.events.filterOptions.venueIds = payload.venueIds;
                    state.events.filterOptions.isLoading = false;
                }
            )
            .addCase(
                getEmployeeParkingEventFilterOptionsData.rejected,
                (state) => {
                    state.events.filterOptions.isLoading = false;
                }
            );
    },
});

export const {updateFilters, updateSearchQuery} = employeeParking.actions;

export default employeeParking.reducer;
